import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, DatabaseBackup, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { listBackups, createBackup, downloadFile } from "@/lib/apiData";

const DATASETS = [
  ["orders", "Orders"], ["users", "Users"], ["dishes", "Dishes"], ["chefs", "Chefs & restaurants"], ["categories", "Food categories"],
  ["blog_posts", "Blog posts"], ["pages", "Web pages"], ["announcements", "Announcements"], ["translations", "Translations"], ["audit_logs", "Audit log"], ["settings", "Settings"],
];
const size = (b) => (b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.ceil(b / 1e3)} KB`);

export default function DataBackup() {
  const qc = useQueryClient();
  const { data: backups = [] } = useQuery({ queryKey: ["backups"], queryFn: listBackups });
  const [busy, setBusy] = useState(false);
  const dl = (url, params) => downloadFile(url, params).catch(() => toast.error("Download failed"));
  const backupNow = async () => {
    setBusy(true);
    try {
      await createBackup();
      toast.success("Backup saved");
      qc.invalidateQueries({ queryKey: ["backups"] });
    } catch {
      toast.error("Backup failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="admin-data-panel">
      <h2 className="font-display text-3xl font-black text-neutral-900">Data export & backup</h2>
      <p className="text-neutral-500 text-sm mt-1 mb-6">Download any data as a spreadsheet (CSV) or JSON file. A full backup is also saved automatically every night at 3:00 (Amsterdam time).</p>

      <div className="bg-white border border-neutral-200 divide-y divide-neutral-100 mb-10">
        {DATASETS.map(([k, label]) => (
          <div key={k} className="flex items-center justify-between px-5 py-3" data-testid={`export-row-${k}`}>
            <span className="text-neutral-800">{label}</span>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => dl(`/admin/export/${k}`, { format: "csv" })} data-testid={`export-${k}-csv`} className="border border-neutral-300 rounded-none h-9 gap-2"><Download className="w-4 h-4" /> CSV</Button>
              <Button variant="ghost" onClick={() => dl(`/admin/export/${k}`, { format: "json" })} data-testid={`export-${k}-json`} className="border border-neutral-300 rounded-none h-9 gap-2"><Download className="w-4 h-4" /> JSON</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
        <h3 className="font-display text-2xl font-bold text-neutral-900">Backups</h3>
        <Button onClick={backupNow} disabled={busy} data-testid="backup-now-btn" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <DatabaseBackup className="w-4 h-4" />} Back up now
        </Button>
      </div>
      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Size</TableHead><TableHead>Records</TableHead><TableHead className="text-right">Download</TableHead></TableRow></TableHeader>
          <TableBody>
            {backups.map((b) => (
              <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                <TableCell className="text-neutral-700">{new Date(b.created_at).toLocaleString()}</TableCell>
                <TableCell><span className="text-xs px-2 py-1 bg-neutral-100 text-neutral-700 capitalize">{b.trigger === "scheduled" ? "Nightly" : "Manual"}</span></TableCell>
                <TableCell className="text-neutral-600">{size(b.size)}</TableCell>
                <TableCell className="text-neutral-600">{Object.values(b.counts || {}).reduce((a, n) => a + n, 0)}</TableCell>
                <TableCell className="text-right"><button onClick={() => dl(`/admin/backups/${b.id}/download`)} data-testid={`backup-download-${b.id}`} className="inline-flex w-8 h-8 items-center justify-center text-neutral-500 hover:text-[#d00000]" aria-label="Download"><Download className="w-4 h-4" /></button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {backups.length === 0 && <p className="p-8 text-center text-neutral-400">No backups yet.</p>}
      </div>
    </div>
  );
}
